import { useTranslation } from 'react-i18next';
import { Box, Divider, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { mdiCheckboxBlank, mdiCheckboxMarked, mdiDelete } from '@mdi/js';
import { motion } from 'framer-motion';
import { useApolloClient } from '@apollo/client';

import {
    INotificationListItemFragment,
    INotificationsNotificationTypeChoices,
    useDeleteNotificationsMutation,
    useSetNotificationReadStatusMutation,
} from 'graphql/types';
import { ListItemButton, ListItemText } from 'common/components/ListItem';
import { getNotificationTypeTranslationKey } from 'common/utils/notification';
import { Typography } from 'common/components/Typography';
import { IconButton } from 'common/components/IconButton';
import { useUnreadNotificationCount } from 'common/hooks/useUnreadNotificationCount';
import { useApolloError } from 'common/hooks/useApolloError';
import { Tooltip } from 'common/components/Tooltip';

interface IProps {
    disableActions?: boolean;
    notification: INotificationListItemFragment;
    onClick?(): void;
    onLoading?(isLoading: boolean): void;
}

export const NotificationListItem = ({
    disableActions,
    notification,
    onClick,
    onLoading,
}: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const { showApolloError } = useApolloError();
    const [setNotificationReadStatus] = useSetNotificationReadStatusMutation();
    const [deleteNotifications, { loading: deleteNotificationsLoading }] =
        useDeleteNotificationsMutation();
    const { updateUnreadNotificationCount } = useUnreadNotificationCount();

    const {
        title,
        content,
        readOn,
        type,
        extraData: extraDataJson,
        creationDate,
    } = notification;
    const notificationTypeTranslationKey =
        getNotificationTypeTranslationKey(type);

    const extraData = JSON.parse(extraDataJson || '{}');
    const { url } = extraData || {};

    const isExternalUrl = useMemo(
        () =>
            [
                INotificationsNotificationTypeChoices.ReportExportCompleted,
                INotificationsNotificationTypeChoices.UserExportCompleted,
            ].includes(type),
        [type]
    );

    /**
     * Update the readOn field in the cache and the unread notification count
     * @param isRead - Whether the notification is read
     * @param isError - Whether the mutation failed
     */
    const updateNotificationReadOn = (isRead: boolean, isError?: boolean) => {
        let readOn = isRead ? new Date().toISOString() : null;
        let count = isRead ? -1 : 1;

        // Use the opposite values for count and readOn to reset if the mutation fails
        if (isError) {
            readOn = isRead ? null : new Date().toISOString();
            count = count === 1 ? -1 : 1;
        }

        client.cache.modify({
            id: client.cache.identify(notification),
            fields: {
                readOn: () => readOn,
            },
        });

        updateUnreadNotificationCount(count);
    };

    const handleSetNotificationReadStatus = async (
        e: React.MouseEvent,
        isRead: boolean
    ) => {
        e.stopPropagation();

        try {
            await setNotificationReadStatus({
                variables: { ids: [notification.id], isRead },
                optimisticResponse: () => {
                    updateNotificationReadOn(isRead);

                    return {
                        __typename: 'Mutations',
                        setNotificationReadStatus: {
                            __typename: 'SetNotificationReadStatus',
                            ok: true,
                        },
                    };
                },
            });
        } catch (error) {
            showApolloError(error);

            updateNotificationReadOn(isRead, true);
        }

        return;
    };

    const handleDeleteNotifications = async (e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            await deleteNotifications({
                variables: { ids: [notification.id] },
                update: (cache) => {
                    cache.evict({ fieldName: 'notifications' });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        // Count is not impacted if already read
        if (readOn) return;

        updateUnreadNotificationCount(-1);

        return;
    };

    const handleClick = async (e: React.MouseEvent) => {
        try {
            await handleSetNotificationReadStatus(e, true);
        } catch (error) {
            showApolloError(error);
        }

        if (isExternalUrl) {
            window.open(url, '_blank');

            return;
        }

        const baseUrl = import.meta.env.VITE_FRONTEND_URL || '';
        const newUrl = url.replace(baseUrl, '');

        navigate(newUrl);

        onClick?.();
    };

    const actionsDisabled = disableActions || deleteNotificationsLoading;

    useEffect(() => {
        // Notify the parent component that the actions are loading
        // to disable the bulk actions
        onLoading?.(deleteNotificationsLoading);
    }, [deleteNotificationsLoading]);

    return (
        <motion.div
            initial="initListItemButton"
            whileHover="hoverListItemButton"
        >
            <ListItemButton
                sx={{
                    px: 2,
                    py: 1,
                    cursor: url ? 'pointer' : 'default',

                    '&:hover': {
                        backgroundColor: !url ? 'transparent' : 'action.hover',
                    },
                }}
                onClick={!!url ? handleClick : undefined}
            >
                <Box sx={{ width: 10, mr: 2 }}>
                    {!readOn && (
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: 'error.main',
                            }}
                        />
                    )}
                </Box>

                <ListItemText showFullSecondary>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                        <Typography
                            color="text.secondary"
                            sx={{ lineHeight: 1.4 }}
                            variant="caption"
                        >
                            {translate(notificationTypeTranslationKey)}
                        </Typography>

                        <Typography
                            color="text.secondary"
                            sx={{ ml: 'auto' }}
                            variant="caption"
                        >
                            {DateTime.fromISO(creationDate).toRelative()}
                        </Typography>
                    </Box>

                    <Stack direction="column" sx={{ pr: 2, spacing: 0.5 }}>
                        <Typography
                            color={readOn ? 'text.secondary' : 'text.primary'}
                            dangerouslySetInnerHTML={{ __html: title }}
                            variant="body2"
                        />

                        {!!content && (
                            <Typography
                                color={
                                    readOn ? 'text.secondary' : 'text.primary'
                                }
                                dangerouslySetInnerHTML={{ __html: content }}
                                sx={{ fontSize: '1.4rem' }}
                                variant="body2"
                            />
                        )}
                    </Stack>

                    <motion.div
                        transition={{ duration: 0.2 }}
                        variants={{
                            initListItemButton: {
                                opacity: 0,
                            },
                            hoverListItemButton: {
                                opacity: 1,
                            },
                        }}
                    >
                        <Stack
                            direction="row"
                            divider={
                                <Divider flexItem orientation="vertical" />
                            }
                            sx={{
                                position: 'absolute',
                                right: '5px',
                                top: '5px',
                                backgroundColor: 'common.white',
                                borderColor: 'grey.300',
                                borderRadius: 1,
                                borderWidth: 1,
                                borderStyle: 'solid',
                            }}
                        >
                            <Tooltip
                                title={
                                    !!readOn
                                        ? translate(
                                              'notificationsDrawer.markAsUnread'
                                          )
                                        : translate(
                                              'notificationsDrawer.markAsRead'
                                          )
                                }
                            >
                                <Box component="span">
                                    <IconButton
                                        disabled={actionsDisabled}
                                        iconPath={
                                            !!readOn
                                                ? mdiCheckboxBlank
                                                : mdiCheckboxMarked
                                        }
                                        iconSize="1.8rem"
                                        onClick={(e: React.MouseEvent) =>
                                            handleSetNotificationReadStatus(
                                                e,
                                                !!readOn ? false : true
                                            )
                                        }
                                    />
                                </Box>
                            </Tooltip>

                            <Tooltip
                                title={translate('notificationsDrawer.delete')}
                            >
                                <Box component="span">
                                    <IconButton
                                        disabled={actionsDisabled}
                                        iconPath={mdiDelete}
                                        iconSize="2rem"
                                        onClick={handleDeleteNotifications}
                                    />
                                </Box>
                            </Tooltip>
                        </Stack>
                    </motion.div>
                </ListItemText>
            </ListItemButton>
        </motion.div>
    );
};
